import React from 'react';
import { cn } from '../../../lib/cn';
import { useFieldControl } from './Field';

/**
 * Textarea, the multi-line text control.
 *
 * Source selectors: `system.css` `textarea.dsk-input, .dsk-textarea`, plus
 * `.dsk-input--error` for the invalid border.
 *
 * The upstream contract is that a textarea wears BOTH classes
 * (`class="dsk-input dsk-textarea"`): `.dsk-input` gives it the surface, the
 * hairline and the focus ring, `.dsk-textarea` releases the fixed control
 * height and turns resize back on vertically only. Horizontal resize would let
 * a user drag a field out of its grid column, so it stays off.
 *
 * Doctrine enforced: same as `Input`. Error is border plus text, never a filled
 * red surface, and it also sets `aria-invalid` so the state is not colour-only.
 */
export const Textarea = React.forwardRef(function Textarea(
  { error = false, rows, className, ...rest },
  ref,
) {
  const field = useFieldControl(rest);

  return (
    <textarea
      ref={ref}
      rows={rows}
      className={cn('dsk-input', 'dsk-textarea', error && 'dsk-input--error', className)}
      {...rest}
      id={field.id}
      aria-describedby={field['aria-describedby']}
      aria-invalid={error ? true : field['aria-invalid']}
    />
  );
});
Textarea.displayName = 'Textarea';

export default Textarea;
